import React, { useState } from 'react'
import { FileUpload } from 'primereact/fileupload'
import { MultiSelect } from 'primereact/multiselect'
import { Button } from 'primereact/button'
import { uploadCsv } from '../../Services/Api'

export default function UserCreation({ form, handleChange }) {
  const [uploading, setUploading] = useState(false)
  const [uploadedFileName, setUploadedFileName] = useState('')
  const [uploadError, setUploadError] = useState('')
  const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

  const userTypeOptions = [
    { label: 'Patient', value: 'patient' },
    { label: 'SM', value: 'sm' },
    { label: 'Eclinic', value: 'eclinic' },
    { label: 'ME', value: 'me' },
  ]

  const onUpload = async (event) => {
    const file = event.files[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadError('Invalid file type. Please upload a .csv file.')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('File size exceeds 5MB limit.')
      return
    }
    setUploadError('')
    setUploading(true)
    try {
      const { data } = await uploadCsv('patients', file)
      const ids = data.values || []
      handleChange(
        'users.patient',
        Array.from(new Set([...(form.users.patient || []), ...ids]))
      )
      setUploadedFileName(file.name)
    } catch (err) {
      console.error('Upload failed:', err)
      setUploadError('Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const removeUploadedFile = () => {
    setUploadedFileName('')
    setUploadError('')
  }

  const removePatientId = (patientId) => {
    const updated = (form.users.patient || []).filter(id => id !== patientId)
    handleChange('users.patient', updated)
  }

  const clearAllPatients = () => {
    handleChange('users.patient', [])
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <h4 className="text-xl font-semibold mb-6">Map Users</h4>

      {/* First row: userType and upload */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* User Types */}
        <div>
          <label className="block mb-2 font-medium text-gray-700">User Types</label>
          <MultiSelect
            value={form.users.userTypes || []}
            options={userTypeOptions}
            onChange={e => handleChange('users.userTypes', e.value)}
            placeholder="Select User Types"
            className="w-full border border-gray-300 rounded p-2"
            display="chip"
          />
        </div>
        {/* CSV Upload: button with filename inline */}
        <div>
          <label className="block mb-2 font-medium text-gray-700">Upload CSV</label>
          <div className="flex items-center gap-2">
            <FileUpload
              mode="basic"
              accept=".csv"
              auto
              chooseLabel={uploading ? 'Uploading…' : 'Upload CSV'}
              uploadHandler={onUpload}
              customUpload
              disabled={uploading}
              className="inline-block"
            />
            {uploadedFileName && (
              <div className="inline-flex items-center bg-gray-100 px-3 py-1 rounded-full">
                <span className="text-gray-800 mr-2">{uploadedFileName}</span>
                <button
                  type="button"
                  onClick={removeUploadedFile}
                  className="text-gray-600 hover:text-gray-800 font-bold"
                >
                  ×
                </button>
              </div>
            )}
          </div>
          {uploadError && <small className="text-red-600 block mt-1">{uploadError}</small>}
        </div>
      </div>

      {/* Second row: current patient IDs */}
      {form.users.patient && form.users.patient.length > 0 && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <div className="flex justify-between items-center mb-4">
            <h5 className="text-lg font-medium text-gray-700">
              Current Patient IDs ({form.users.patient.length})
            </h5>
            <Button
              label="Clear All"
              onClick={clearAllPatients}
              className="p-button-sm p-button-outlined p-button-danger"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            {form.users.patient.map((id, idx) => (
              <div
                key={idx}
                className="flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
              >
                <span className="mr-2">{id}</span>
                <button
                  type="button"
                  onClick={() => removePatientId(id)}
                  className="text-green-600 hover:text-green-800 font-bold"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
